import {
  getLivingPathway,
  isLivingEntryRoute,
  isLivingOptionRoute,
  isSubsectionOrPropertyRoute,
} from '@/utils/livingRoute'

export default {
  namespaced: true,
  state: {
    hasUserClosedSlideOut: false,
    isSlideOutVisible: false,
    sidebarCloseTimeout: null
  },

  mutations: {
    CLOSE_SLIDEOUT (state) {
      state.isSlideOutVisible = false
      state.hasUserClosedSlideOut = true
    },

    OPEN_SLIDEOUT (state) {
      state.isSlideOutVisible = true
      state.hasUserClosedSlideOut = false
    },

    SET_SIDEBAR_CLOSE_TIMEOUT (state, timeout) {
      state.sidebarCloseTimeout = timeout
    },

    CLEAR_SIDEBAR_CLOSE_TIMEOUT (state) {
      clearTimeout(state.sidebarCloseTimeout)
    }
  },

  getters: {
    activeSidebar (state, getters, rootState, rootGetters) {
      const routeName = rootState.route.name
      const pathway = getLivingPathway(rootState.route)

      if (routeName === 'page' && rootGetters.activePage) {
        return {
          description: rootGetters.activePage.description[rootState.language.code] || rootGetters.activePage.description['en'],
          filterType: '',
          heading: rootGetters.activePage.heading[rootState.language.code] || rootGetters.activePage.heading['en'],
          markerGuide: false,
          minimap: rootGetters.activePage.minimapImage,
          qrMessage: rootGetters.activePage.qrMessage,
          qrUrl: rootGetters.activePage.qrUrl,
          virtualTourUrl: rootGetters.activePage.virtualTourUrl,
          virtualTourButtonLabel: rootGetters.activePage.virtualTourButtonLabel,
          issuuEmbedUrl: rootGetters.activePage.issuuEmbedUrl,
        }
      }

      if (isLivingEntryRoute(routeName) && rootGetters.config) {
        return {
          description: rootGetters.config.liveHereDescription[rootState.language.code] || rootGetters.config.liveHereDescription['en'],
          filterType: '',
          heading: rootGetters.config.liveHereHeading[rootState.language.code] || rootGetters.config.liveHereHeading['en'],
          markerGuide: false,
          minimap: rootGetters.config.liveHereSidebarMinimap,
          qrMessage: '',
          qrUrl: '',
        }
      }

      if (isLivingOptionRoute(routeName) && rootGetters['property/activeLivingOption']) {
        return {
          description: rootGetters['property/activeLivingOption'].description ? rootGetters['property/activeLivingOption'].description[rootState.language.code] || rootGetters['property/activeLivingOption'].description['en'] : '',
          filterType: pathway,
          heading: rootGetters['property/activeLivingOption'].displayName[rootState.language.code] || rootGetters['property/activeLivingOption'].displayName['en'] || rootGetters['property/activeLivingOption'].label,
          markerGuide: false,
          minimap: rootGetters['property/activeLivingOption'].sidebarMinimap,
          qrMessage: '',
          qrUrl: '',
          isFilterHidden: rootGetters['property/activeLivingOption'].isFilterHidden,
          iframes: rootGetters['property/activeLivingOption'].iframes
        }
      }    

      if (isSubsectionOrPropertyRoute(routeName) && rootGetters['property/activeSubsection']) {
        const useSidebarSubnavigation = rootState.newConfig.general.useSidebarSubnavigation
        const activeLivingOption = rootGetters['property/activeLivingOption']
        const activeSubsection = rootGetters['property/activeSubsection']

        let parent = null
        if (activeSubsection.parent.type === 'subsection') {
          parent = rootState.property.subsections.find(subsection => subsection._id === activeSubsection.parent.id)
        }

        if (activeSubsection.parent.type === 'livingOption') {
          parent = activeLivingOption
        }

        let parentHeading = activeLivingOption.displayName[rootState.language.code]

        const activeSubsectionHeading = rootGetters['property/activeSubsection'].displayName[rootState.language.code] || rootGetters['property/activeSubsection'].displayName['en'] || rootGetters['property/activeSubsection'].label

        // Nested subsection
        if (activeSubsection.subsections.length > 0) {
          parentHeading = activeSubsectionHeading
        }

        // End of the subsection nesting
        if (activeSubsection.subsections.length === 0) {
          parentHeading = parent ? parent.displayName[rootState.language.code] : activeSubsectionHeading
        }

        return {
          description: rootGetters['property/activeSubsection'].description ? rootGetters['property/activeSubsection'].description[rootState.language.code] || rootGetters['property/activeSubsection'].description['en'] : '',
          filterType: pathway,
          heading: useSidebarSubnavigation ? `${parentHeading}` : activeSubsectionHeading,
          markerGuide: rootGetters['property/activeSubsection'].properties.length ? true : false,
          minimap: rootGetters['property/activeSubsection'].minimapImage,
          qrMessage: '',
          qrUrl: '',
          isFilterHidden: rootGetters['property/activeSubsection'].isFilterHidden,
          iframes: rootGetters['property/activeSubsection'].iframes,
          subsectionShortcuts: rootGetters['property/activeSubsection'].subsectionShortcuts
        }
      }

      if (['live-display-homes', 'live-display-homes-property'].includes(routeName) && rootGetters['property/activeSubsection']) {        
        return {
          description: rootGetters['property/activeSubsection'].description ? rootGetters['property/activeSubsection'].description[rootState.language.code] || rootGetters['property/activeSubsection'].description['en'] : '',
          filterType: pathway,
          heading: rootGetters['property/activeSubsection'].displayName[rootState.language.code] || rootGetters['property/activeSubsection'].displayName['en'] || rootGetters['property/activeSubsection'].label,
          markerGuide: false,
          minimap: rootGetters['property/activeSubsection'].minimapImage,
          qrMessage: '',
          qrUrl: '',
          isFilterHidden: rootGetters['property/activeSubsection'].isFilterHidden,
          iframes: rootGetters['property/activeSubsection'].iframes,
          subsectionShortcuts: rootGetters['property/activeSubsection'].subsectionShortcuts
        }
      }

      if (['packages', 'package-modal'].includes(routeName) && rootGetters.config) {
        return {
          description: rootGetters.config.liveHerePackagesSidebarDesc[rootState.language.code] || rootGetters.config.liveHerePackagesSidebarDesc['en'],
          filterType: 'package',
          heading: rootGetters.config.liveHerePackagesSidebarHeading[rootState.language.code] || rootGetters.config.liveHerePackagesSidebarHeading['en'],
          markerGuide: false,
          minimap: '',
          qrMessage: '',
          qrUrl: '',
        }
      }

      if (['dwellings', 'dwelling-modal'].includes(routeName) && rootGetters.config) {
        return {
          description: rootGetters.config.liveHerePackagesSidebarDesc[rootState.language.code] || rootGetters.config.liveHerePackagesSidebarDesc['en'],
          filterType: 'dwelling',
          heading: rootGetters.config.liveHerePackagesSidebarHeading[rootState.language.code] || rootGetters.config.liveHerePackagesSidebarHeading['en'],
          markerGuide: false,
          minimap: '',
          qrMessage: '',
          qrUrl: '',
        }
      }

      if (['favourites', 'favourite-modal'].includes(routeName) && rootGetters.config) {
        return {
          description: rootGetters.config.liveHereFavouritesSidebarDesc[rootState.language.code] || rootGetters.config.liveHereFavouritesSidebarDesc['en'],
          filterType: '',
          heading: rootGetters.config.liveHereFavouritesSidebarHeading[rootState.language.code] || rootGetters.config.liveHereFavouritesSidebarHeading['en'],
          markerGuide: false,
          minimap: '',
          qrMessage: '',
          qrUrl: rootGetters['favourites/qrcode'],
        }
      }

      return {}
    },
    
    isSlideOutVisible (state) {
      return state.isSlideOutVisible
    },

    shouldSlideOutAutoOpen (state) {
      return !state.hasUserClosedSlideOut && !state.isSlideOutVisible
    }
  }
}
